import { useMemo, useState } from "react";
import { Popover, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { PeriodPickerContent } from "./PeriodPickerContent";
import { PeriodPickerTrigger } from "./PeriodPickerTrigger";
import {
  type CampaignImpactPeriodOption,
  formatMonthLabel,
  getAllPeriodOptions,
  toPeriodKey,
} from "../../../constants";
import type { I18nChildContext } from "@bookingcom/lingojs-react";

const getOptionLabel = (option: CampaignImpactPeriodOption, i18n: I18nChildContext, now?: Date): string => {
  if (option.group === "recent") {
    return i18n.trans(t("flights_airline_portal_campaign_date_picker_options_one", { variables: option.labelVars }));
  }
  const monthLabel = formatMonthLabel(option.periodValue, now);
  if (option.periodValue === 1) {
    return `${i18n.trans(t("flights_airline_portal_campaign_date_picker_options_three"))} (${monthLabel})`;
  }
  return monthLabel;
};

type PeriodPickerProps = {
  period: string;
  onChange: (period: string) => void;
  now?: Date;
};

export const PeriodPicker = ({ onChange, now, ...props }: PeriodPickerProps) => {
  const i18n = useI18n();
  const [active, setActive] = useState(false);

  const allOptions = useMemo(() => getAllPeriodOptions(now), [now]);

  const periodOptionGroups = useMemo(() => {
    const groups: Record<string, { label: string; options: { value: string; text: string }[] }> = {};

    for (const option of allOptions) {
      if (!groups[option.group]) {
        const label =
          option.group === "recent"
            ? i18n.trans(t("flights_airline_portal_campaign_date_picker_header_one"))
            : i18n.trans(t("flights_airline_portal_campaign_date_picker_header_two"));
        groups[option.group] = { label, options: [] };
      }

      groups[option.group].options.push({
        value: toPeriodKey(option.periodType, option.periodValue),
        text: getOptionLabel(option, i18n, now),
      });
    }

    return Object.values(groups);
  }, [i18n, allOptions, now]);

  const selectedText = useMemo(() => {
    const option = allOptions.find((o) => toPeriodKey(o.periodType, o.periodValue) === props.period)!;
    return getOptionLabel(option, i18n, now);
  }, [i18n, props.period, allOptions, now]);

  const handleChange = (period: string) => {
    onChange(period);
    setActive(false);
  };

  return (
    <Stack gap={1}>
      <Text variant="emphasized_2">{i18n.trans(t("flights_airline_portal_campaign_date_picker_label"))}</Text>
      <Popover
        closeAriaLabel="Close popover"
        position="bottom-start"
        forcePosition={true}
        hideArrow={true}
        hideClose={true}
        keepMounted={false}
        size="stretch"
        fill
        active={active}
        onOpen={() => setActive(true)}
        onClose={() => setActive(false)}
      >
        <Popover.Trigger>
          {(attributes) => <PeriodPickerTrigger text={selectedText} attributes={attributes} />}
        </Popover.Trigger>
        <Popover.Content>
          <PeriodPickerContent
            period={props.period}
            periodOptionGroups={periodOptionGroups}
            onPeriodChange={handleChange}
          />
        </Popover.Content>
      </Popover>
    </Stack>
  );
};
